/**
 * Outcome Trend — Phase 8b monthly time-series of calibrated projected
 * outcomes (Medi-Cal enrollments + nav supports for CCHI; ECM enrollment
 * advance + connector contacts for IFR/Sycamores; CHW callbacks + service
 * touchpoints for VyC). Lines per federation, toggled by chip selector.
 *
 * Internal CBO only.
 */

import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  useFederationRollup,
  useOutcomeMonthly,
} from '@/hooks/ml';

const FED_COLOR: Record<string, string> = {
  CCHI: '#6366f1',
  IFR: '#10b981',
  SYCAMORES: '#f59e0b',
  VYC: '#ec4899',
};

export const OutcomeTrendCard: React.FC = () => {
  const fed = useFederationRollup();
  const [selectedFed, setSelectedFed] = useState<string | null>(null);
  const trend = useOutcomeMonthly(selectedFed, 36);
  const [metric, setMetric] = useState<'projected_enrollments' | 'projected_nav_supports' | 'sends'>('projected_enrollments');

  const federations = fed.data?.federations.map((f) => f.federation_code) ?? [];
  const activeFed = selectedFed ?? federations[0] ?? null;

  const series = useMemo(() => {
    if (!trend.data?.totals?.length) return [];
    return trend.data.totals
      .filter((r) => !activeFed || r.federation_code === activeFed)
      .map((r) => ({
        month: r.month.slice(0, 7),
        sends: Number(r.sends) || 0,
        replies: Number(r.replies) || 0,
        clicks: Number(r.clicks) || 0,
        projected_enrollments: Number(r.projected_enrollments) || 0,
        projected_nav_supports: Number(r.projected_nav_supports) || 0,
      }));
  }, [trend.data, activeFed]);

  const totals = useMemo(() => {
    return series.reduce(
      (a, r) => ({
        sends: a.sends + r.sends,
        replies: a.replies + r.replies,
        clicks: a.clicks + r.clicks,
        projected_enrollments: a.projected_enrollments + r.projected_enrollments,
        projected_nav_supports: a.projected_nav_supports + r.projected_nav_supports,
      }),
      { sends: 0, replies: 0, clicks: 0, projected_enrollments: 0, projected_nav_supports: 0 }
    );
  }, [series]);

  if (trend.isError) {
    return (
      <Card>
        <CardContent className="pt-4 text-sm text-gray-500">
          Outcome trend unavailable (internal CBO only).
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">Federation:</span>
            {federations.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setSelectedFed(f)}
                className={`rounded-full border px-2.5 py-0.5 font-medium ${
                  f === activeFed
                    ? 'border-indigo-300 bg-indigo-50 text-indigo-900'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 text-xs">
            <span className="text-gray-500">Metric:</span>
            {[
              { v: 'projected_enrollments' as const, label: 'Enrollments' },
              { v: 'projected_nav_supports' as const, label: 'Nav supports' },
              { v: 'sends' as const, label: 'Sends' },
            ].map((m) => (
              <button
                key={m.v}
                type="button"
                onClick={() => setMetric(m.v)}
                className={`rounded-full border px-2.5 py-0.5 font-medium ${
                  m.v === metric
                    ? 'border-indigo-300 bg-indigo-50 text-indigo-900'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 md:grid-cols-5">
          <Mini label="Sends" value={totals.sends.toLocaleString()} />
          <Mini label="Replies" value={totals.replies.toLocaleString()} />
          <Mini label="Clicks" value={totals.clicks.toLocaleString()} />
          <Mini label="Enrollments (proj)" value={totals.projected_enrollments.toLocaleString()} tone="positive" />
          <Mini label="Nav supports (proj)" value={totals.projected_nav_supports.toLocaleString()} tone="positive" />
        </div>

        {trend.isLoading ? (
          <div className="h-64 flex items-center justify-center text-sm text-gray-500">Loading trend…</div>
        ) : !series.length ? (
          <div className="h-64 flex items-center justify-center text-sm text-gray-500">
            No monthly data for the selected federation.
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" tick={{ fontSize: 11 }} minTickGap={24} />
                <YAxis
                  stroke="#6b7280"
                  tick={{ fontSize: 11 }}
                  width={56}
                  tickFormatter={(v: number) =>
                    v >= 1000 ? `${(v / 1000).toFixed(v >= 10_000 ? 0 : 1)}k` : `${v}`
                  }
                />
                <Tooltip
                  formatter={(value: number | string) => Number(value).toLocaleString()}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey={metric}
                  name={
                    metric === 'projected_enrollments'
                      ? 'Projected enrollments'
                      : metric === 'projected_nav_supports'
                      ? 'Projected nav supports'
                      : 'Sends'
                  }
                  stroke={FED_COLOR[activeFed ?? ''] ?? '#6366f1'}
                  strokeWidth={2.5}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="flex items-start gap-1.5 text-[10px] text-gray-500">
          <TrendingUp className="h-3 w-3 flex-shrink-0 mt-0.5" />
          Projected outcomes apply the active calibration ratio for each federation
          (CCHI: 1.07/0.93 from client-reported 2024 totals; IFR/Sycamores: CHCS
          ECM proxy 0.60/0.75; VyC: CHW outreach proxy 1.20/0.85). Proxy ratios
          are directional only — replace with reported actuals when available.
        </div>
      </CardContent>
    </Card>
  );
};

const Mini: React.FC<{ label: string; value: string; tone?: 'positive' }> = ({
  label,
  value,
  tone,
}) => (
  <div
    className={`rounded border p-2 ${
      tone === 'positive' ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200 bg-white'
    }`}
  >
    <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
    <div className="mt-0.5 text-sm font-semibold text-gray-900 tabular-nums">{value}</div>
  </div>
);

export default OutcomeTrendCard;
